/**
 * Which analytics plot keys can actually be shown as a tab.
 *
 * Kept free of React and react-bootstrap imports so the logic can be
 * unit tested on its own.
 */

/**
 * One entry of a plot config. Only `additionalPlot` is read here; the
 * rest (title, colour, series definitions) varies per platform, hence
 * the open index signature. Entries may be null.
 */
export type PlotTabConfigEntry =
  | ({ additionalPlot?: boolean } & Record<string, unknown>)
  | null
  | undefined;

export type PlotTabConfig = Record<string, PlotTabConfigEntry>;

/**
 * The plot keys that get a tab of their own.
 *
 * Both the tab bar and the tab panes are driven by `config`, so only a
 * key present there can ever render a pane. Two kinds of key must be
 * excluded from selection:
 *
 * - Keys the backend sends that this frontend has no config for. New
 *   metrics ship server-side first, so the analytics document routinely
 *   carries plots we cannot draw yet.
 * - Keys marked `additionalPlot`, which are merged into another tab's
 *   chart instead of getting a tab of their own.
 *
 * Selecting either leaves the active key pointing at a pane that does
 * not exist, and the panel renders empty. Iterating `config` rather than
 * the data keys also means the result already matches the order the tabs
 * render in, so no sorting is needed.
 */

export const getSelectableTabKeys = (
  config: PlotTabConfig | null | undefined,
  plots: Record<string, unknown> | null | undefined
): string[] => {
  if (!config || !plots) return [];
  const plotKeys = new Set(Object.keys(plots));
  return Object.keys(config).filter(
    (key) => !config[key]?.additionalPlot && plotKeys.has(key)
  );
};
